'use client';

import Link from 'next/link';

interface ContentOutageNoticeProps {
  /** What the reader was trying to read, e.g. "article" or "help centre". */
  what: string;
  /** Where "browse everything" should go, e.g. `/blog`. */
  indexHref: string;
  indexLabel: string;
  reset: () => void;
}

/**
 * Failure state for the Mongo-backed public content surfaces.
 *
 * These pages read blog / help / changelog content straight from Mongo with no
 * cache in front, so a database blip renders them unreadable. Before this they
 * fell through to the root `app/error.tsx`, which is branded but written for a
 * signed-in user hitting a bug: "An unexpected error occurred" plus a Contact
 * Support link. That is the wrong thing to tell an anonymous reader about a
 * transient outage — it reads as "this page is broken, please report it", and
 * invites support tickets nobody needs to file.
 *
 * The copy here says what is actually true — it is temporary, try again — and
 * offers a way back into the section instead of a support form (RELM-350).
 */
export function ContentOutageNotice({
  what,
  indexHref,
  indexLabel,
  reset,
}: ContentOutageNoticeProps) {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        <h1 className="mb-2 text-xl font-semibold text-gray-900">
          This {what} isn&apos;t loading right now
        </h1>
        <p className="mb-6 text-sm text-gray-500">
          It&apos;s a temporary problem on our side, not something you did.
          Trying again usually works.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <button
            onClick={reset}
            className="bg-brand-600 hover:bg-brand-700 focus:ring-brand-500 rounded-lg px-5 py-2 text-sm font-medium text-white focus:ring-2 focus:ring-offset-2 focus:outline-none"
          >
            Try again
          </button>
          <Link
            href={indexHref}
            className="rounded-lg bg-gray-100 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:outline-none"
          >
            {indexLabel}
          </Link>
        </div>
      </div>
    </div>
  );
}
